import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Star, MessageSquare, User, ThumbsUp, AlertCircle, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StarRating = ({ rating, onRatingChange, readonly = false, size = "md" }) => {
  const [hoverRating, setHoverRating] = useState(0);
  const sizeClass = size === "sm" ? "h-4 w-4" : size === "lg" ? "h-6 w-6" : "h-5 w-5";

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          onClick={() => onRatingChange && onRatingChange(star)}
          onMouseEnter={() => !readonly && setHoverRating(star)}
          onMouseLeave={() => !readonly && setHoverRating(0)}
          className={`${readonly ? "cursor-default" : "cursor-pointer hover:scale-110"} transition-transform`}
        >
          <Star
            className={`${sizeClass} ${
              star <= (hoverRating || rating)
                ? "fill-[#c9a227] text-[#c9a227]"
                : "fill-slate-200 text-slate-200"
            }`}
          />
        </button>
      ))}
    </div>
  );
};

export default function PropertyReviews({ propertyId, propertyTitle }) {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [newReview, setNewReview] = useState({
    rating: 5,
    title: "",
    comment: ""
  });

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isGuest = user.role === "guest" || !user.id;

  useEffect(() => {
    fetchReviews();
    fetchStats();
  }, [propertyId]);

  const fetchReviews = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/reviews?property_id=${propertyId}&review_type=property_review`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviews(response.data);
    } catch (error) {
      console.error("Failed to fetch reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/reviews/property/${propertyId}/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch review stats:", error);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    if (isGuest) {
      toast.error("Please login to submit a review");
      return;
    }

    if (!newReview.comment.trim()) {
      toast.error("Please write a comment");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/reviews`, {
        review_type: "property_review",
        property_id: propertyId,
        rating: newReview.rating,
        title: newReview.title || null,
        comment: newReview.comment
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.moderation_status === "rejected") {
        toast.warning(response.data.message || "Your review is pending approval");
      } else {
        toast.success("Review submitted successfully!");
        fetchReviews();
        fetchStats();
      }
      
      setNewReview({ rating: 5, title: "", comment: "" });
      setShowForm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 bg-gradient-to-r from-[#0f2744] to-[#1e3a5f]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MessageSquare className="h-5 w-5 text-[#c9a227]" />
            <h3 className="text-lg font-semibold text-white">Reviews & Ratings</h3>
          </div>
          {!isGuest && !showForm && (
            <Button
              onClick={() => setShowForm(true)}
              size="sm"
              className="bg-[#c9a227] hover:bg-[#b08d26] text-white"
              data-testid="write-review-btn"
            >
              Write a Review
            </Button>
          )}
        </div>
      </div>

      <div className="p-6">
        {/* Stats Summary */}
        {stats && stats.total_reviews > 0 && (
          <div className="flex items-center gap-6 mb-6 pb-6 border-b border-slate-200">
            <div className="text-center">
              <p className="text-4xl font-bold text-[#0f2744]">{stats.average_rating}</p>
              <StarRating rating={Math.round(stats.average_rating)} readonly size="sm" />
              <p className="text-sm text-slate-500 mt-1">{stats.total_reviews} reviews</p>
            </div>
            <div className="flex-1 space-y-1">
              {[5, 4, 3, 2, 1].map((star) => (
                <div key={star} className="flex items-center gap-2">
                  <span className="text-xs text-slate-600 w-3">{star}</span>
                  <Star className="h-3 w-3 fill-[#c9a227] text-[#c9a227]" />
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#c9a227] rounded-full"
                      style={{
                        width: `${(stats.rating_distribution[star] / stats.total_reviews) * 100}%`
                      }}
                    />
                  </div>
                  <span className="text-xs text-slate-500 w-6">{stats.rating_distribution[star]}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Guest Notice */}
        {isGuest && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-amber-800 font-medium">Login required</p>
              <p className="text-xs text-amber-700">Please login or register to write a review for this property.</p>
            </div>
          </div>
        )}

        {/* Review Form */}
        {showForm && (
          <form onSubmit={handleSubmitReview} className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <h4 className="font-semibold text-[#0f2744] mb-4">Write Your Review</h4>
            
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-slate-600">Your Rating</Label>
                <div className="mt-1">
                  <StarRating
                    rating={newReview.rating}
                    onRatingChange={(rating) => setNewReview({ ...newReview, rating })}
                    size="lg"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="review-title" className="text-sm text-slate-600">Title (Optional)</Label>
                <Input
                  id="review-title"
                  placeholder="Summarize your experience"
                  value={newReview.title}
                  onChange={(e) => setNewReview({ ...newReview, title: e.target.value })}
                  className="mt-1"
                  data-testid="review-title-input"
                />
              </div>

              <div>
                <Label htmlFor="review-comment" className="text-sm text-slate-600">Your Review *</Label>
                <Textarea
                  id="review-comment"
                  placeholder="Share your experience with this property..."
                  value={newReview.comment}
                  onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                  className="mt-1 min-h-[100px]"
                  required
                  data-testid="review-comment-input"
                />
              </div>

              <div className="flex gap-2 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-[#0f2744] hover:bg-[#1e3a5f]"
                  data-testid="submit-review-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Submit Review
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        )}

        {/* Reviews List */}
        {loading ? (
          <div className="text-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-[#0f2744] mx-auto" />
            <p className="text-sm text-slate-500 mt-2">Loading reviews...</p>
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-8">
            <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No reviews yet</p>
            <p className="text-sm text-slate-400">Be the first to review this property!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {reviews.map((review) => (
              <div
                key={review.id}
                className="p-4 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors"
                data-testid={`review-${review.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#0f2744] flex items-center justify-center">
                      <User className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-[#0f2744]">{review.user_name}</p>
                      <p className="text-xs text-slate-500">{formatDate(review.created_at)}</p>
                    </div>
                  </div>
                  <StarRating rating={review.rating} readonly size="sm" />
                </div>

                {review.title && (
                  <h5 className="font-semibold text-slate-800 mb-1">{review.title}</h5>
                )}
                <p className="text-sm text-slate-600 leading-relaxed">{review.comment}</p>

                {review.admin_response && (
                  <div className="mt-3 p-3 bg-[#f0f4f8] rounded-lg border-l-4 border-[#0f2744]">
                    <p className="text-xs text-slate-500 mb-1">Response from CAPIN Realty</p>
                    <p className="text-sm text-slate-700">{review.admin_response}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
